import { useState } from "react";
import axios, { AxiosError } from "axios";
import { Table } from "react-bootstrap";
import { Variant } from "react-bootstrap/esm/types";
import { DateTime } from "luxon";

import ErrorPage from "../ErrorPage";
import PageLoader from "../PageLoader";
import StatusBadge from "../StatusBadge";
import {
  CampTransactionsType,
  CampTransactionType,
  CampTransactionStatus,
  CampTransactionMoney,
} from "../../types/campTransaction";
import styles from "./index.module.scss";

type Props = {
  data?: CampTransactionsType;
  error?: Error | null;
  isLoading: boolean;
};

const statusEmphasis: Record<CampTransactionStatus, Variant> = {
  ACTUAL: "success",
  PENDING: "warning",
  CONFIRMED: "success",
  SETTLED: "success",
  CANCELLED: "secondary",
  FAILED: "danger",
};

const formatDecimal = (val: string | null | undefined) => {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (Number.isNaN(n)) return val;
  return parseFloat(n.toFixed(3)).toString();
};

const formatMoney = (amount: CampTransactionMoney | null | undefined) => {
  if (!amount) return "—";
  const quantity = formatDecimal(amount.quantity);
  return amount.currency ? `${amount.currency} ${quantity}` : quantity;
};

const formatBasePrice = (amount: CampTransactionMoney | null | undefined) => {
  if (!amount) return "—";
  const quantity = amount.quantity?.trim();
  if (!quantity) return "—";
  return amount.currency ? `${amount.currency} ${quantity}` : quantity;
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  return dt.isValid ? dt.toFormat("dd MMM yyyy HH:mm") : iso;
};

const CopyCell = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className={styles.copyCell}>
      <span className={styles.copyValue}>{value}</span>
      <button className={styles.copyBtn} onClick={handleCopy} title="Copy">
        <span className="material-icons" style={{ fontSize: 14 }}>
          {copied ? "check" : "content_copy"}
        </span>
      </button>
    </div>
  );
};

const TableHead = () => (
  <thead>
    <tr>
      <th>ID</th>
      <th>Portfolio ID</th>
      <th>Security ISIN</th>
      <th>Type</th>
      <th>Trade Date</th>
      <th>Settlement Date</th>
      <th>Value Date</th>
      <th>Value Date Confirmed</th>
      <th>Units</th>
      <th>Gross Amount</th>
      <th>Base Price</th>
      <th>Base Currency</th>
      <th>Status</th>
      <th>Category</th>
      <th>Created At</th>
      <th>Modified At</th>
    </tr>
  </thead>
);

const TableRow = ({ tx }: { tx: CampTransactionType }) => (
  <tr>
    <td>
      <CopyCell value={tx.id} />
    </td>
    <td>
      <CopyCell value={tx.portfolioId} />
    </td>
    <td>
      <CopyCell value={tx.securityISIN} />
    </td>
    <td>{tx.transactionType}</td>
    <td>{formatDate(tx.tradeDate)}</td>
    <td>{formatDate(tx.settlementDate)}</td>
    <td>{formatDate(tx.valueDate)}</td>
    <td>{formatDate(tx.valueDateConfirmed)}</td>
    <td>{formatDecimal(tx.units)}</td>
    <td>{formatMoney(tx.grossAmount)}</td>
    <td>{formatBasePrice(tx.basePrice)}</td>
    <td>{tx.baseCurrency}</td>
    <td>
      <StatusBadge
        status={tx.transactionStatus}
        emphasis={statusEmphasis[tx.transactionStatus]}
      />
    </td>
    <td>{tx.category ?? "—"}</td>
    <td>{formatDate(tx.createdAt)}</td>
    <td>{formatDate(tx.modifiedAt)}</td>
  </tr>
);

const TableBody = ({
  transactions,
}: {
  transactions: CampTransactionType[];
}) => (
  <tbody>
    {transactions.length > 0 ? (
      transactions.map((tx) => <TableRow key={tx.id} tx={tx} />)
    ) : (
      <tr>
        <td colSpan={16} className={styles.emptyTable}>
          Oops, no results were found.
        </td>
      </tr>
    )}
  </tbody>
);

const CampTransactionTable = ({ data, error, isLoading }: Props) => {
  if (isLoading)
    return (
      <PageLoader isLoading={isLoading} content="Loading transactions..." />
    );

  if (error || !data) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        errorCode: string;
        errorMessage: string;
      }>;
      return (
        <ErrorPage
          title={axiosError.response?.data?.errorCode}
          message={axiosError.response?.data?.errorMessage}
        />
      );
    }
    return <ErrorPage />;
  }

  return (
    <div className={styles.table}>
      <Table hover bordered responsive="md">
        <TableHead />
        <TableBody transactions={data.transactions} />
      </Table>
    </div>
  );
};

export default CampTransactionTable;
